// generate_sheet.jsx — "Build a workout": inputs + a live, explainable plan.
//
// The plan is recomputed (pure, deterministic: generator.js) whenever an input
// changes, so the sheet never shows a stale or fabricated workout. Last choices
// live in prefs.generator; equipment, avoided exercises and injuries live in the
// training profile so the coach and program builder see the same answers.
//
//   GenerateWorkoutSheet({ open, onClose })   the sheet
//   BuildWorkoutCard({ onOpen })              Home entry card (with a live preview line)
//   useGeneratorOpts()                        remembered inputs

import React from 'react';
import { LLSheet } from './sheets.jsx';
import { fmtWeight, unitLabel, useApp } from './store.jsx';
import { LL, LLIcon } from './tokens.jsx';
import { MuscleThumb } from './musclemap.jsx';
import { unlockAudioContext } from './timers.jsx';
import {
  generateWorkout, alternativesFor, planToSlots, resolveEquipment, equipmentPresetOf,
  TIME_OPTIONS, GOAL_OPTIONS, FOCUS_OPTIONS, EQUIPMENT_PRESETS, EQUIPMENT_TAGS, GEN_MUSCLES, injuryTags, INJURY_LABEL,
} from './generator.js';
import { slotId } from './session_model.js';

const kicker = { fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.1, color: LL.fgDim, textTransform: 'uppercase' };
const label = { fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.fgMute };
const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`;
const fmtRest = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const reduced = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };

const ShuffleIcon = (p) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" {...p}>
    <path d="M3 6h3.2c1.6 0 2.6.7 3.5 2l1.6 2.4c.9 1.3 1.9 2 3.5 2H17M3 14h3.2c1.6 0 2.6-.7 3.5-2M13 6h1.8H17M14.8 3.8L17 6l-2.2 2.2M14.8 10.2L17 12.4l-2.2 2.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ClockIcon = (p) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" {...p}>
    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6" /><path d="M10 6v4.2l2.6 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const SparkIcon = LLIcon.ai;

// ——— Remembered inputs ——————————————————————————————————————————————————————

export function useGeneratorOpts() {
  const { state, actions } = useApp();
  const g = state.prefs.generator || {};
  const p = state.profile || {};
  const defGoal = p.goal === 'strength' ? 'strength' : (p.goal === 'general' || p.goal === 'fatloss') ? 'general' : 'hypertrophy';
  const defMin = TIME_OPTIONS.includes(p.sessionMinutes) ? p.sessionMinutes : 45;
  const opts = {
    minutes: TIME_OPTIONS.includes(g.minutes) ? g.minutes : defMin,
    goal: GOAL_OPTIONS.some(x => x.id === g.goal) ? g.goal : defGoal,
    focus: FOCUS_OPTIONS.some(x => x.id === g.focus) ? g.focus : 'auto',
    muscles: Array.isArray(g.muscles) ? g.muscles.filter(m => GEN_MUSCLES.includes(m)) : [],
  };
  const set = React.useCallback((patch) => actions.setPref('generator', { ...(state.prefs.generator || {}), ...patch }), [actions, state.prefs.generator]);
  return [opts, set];
}

// ——— Home card ——————————————————————————————————————————————————————————————

export function BuildWorkoutCard({ onOpen, style }) {
  const { state } = useApp();
  const [opts] = useGeneratorOpts();
  // A cheap preview of what the builder would focus on right now (real data).
  const preview = React.useMemo(() => {
    try {
      const plan = generateWorkout(state, { ...opts, skipReadiness: true });
      const ms = [...new Set(plan.items.map(i => i.muscle))].slice(0, 3);
      return { ms, n: plan.items.length, min: plan.estMinutes };
    } catch { return null; }
  }, [state.sessions, state.exercises, state.profile, state.prefs.generator, state.prefs.units]); // eslint-disable-line react-hooks/exhaustive-deps
  const sub = preview && preview.n
    ? `${preview.ms.join(', ')} · ~${preview.min} min`
    : 'Fits your time, equipment and recovery';
  return (
    <button onClick={onOpen} style={{
      width: 'calc(100% - 32px)', margin: '12px 16px 0', padding: 14, display: 'flex', alignItems: 'center', gap: 12,
      background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, cursor: 'pointer', textAlign: 'left', ...style,
    }}>
      <span style={{ width: 40, height: 40, borderRadius: LL.r.md, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: LL.accentSoft, color: LL.accentText }}>
        <SparkIcon style={{ width: 22, height: 22 }} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg }}>Build a workout</div>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
      </span>
      <LLIcon.chev style={{ color: LL.fgFaint, flexShrink: 0 }} />
    </button>
  );
}

// ——— Primitives ——————————————————————————————————————————————————————————————

function Chip({ on, children, onClick, ariaLabel }) {
  return (
    <button onClick={onClick} aria-pressed={!!on} aria-label={ariaLabel} style={{
      height: 34, padding: '0 13px', borderRadius: LL.r.pill, cursor: 'pointer', flexShrink: 0,
      border: `1px solid ${on ? 'transparent' : LL.line}`,
      background: on ? `color-mix(in oklab, ${LL.accent} 18%, ${LL.surface})` : LL.surface2,
      color: on ? LL.accentText : LL.fgMute,
      fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 6,
      transition: `background 200ms ${LL.ease}, color 200ms ${LL.ease}`,
    }}>{on && <LLIcon.check style={{ width: 13, height: 13 }} />}{children}</button>
  );
}

// Same look as Settings' segmented control (kept local so Home doesn't pull the settings bundle).
function SSSegmented({ options, value, onChange, full }) {
  return (
    <div role="radiogroup" style={{ display: full ? 'flex' : 'inline-flex', padding: 2, gap: 2, borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}` }}>
      {options.map(([v, text]) => {
        const on = v === value;
        return (
          <button key={String(v)} role="radio" aria-checked={on} onClick={() => onChange(v)} style={{
            flex: full ? 1 : undefined, height: 36, minWidth: 44, padding: '0 8px', borderRadius: LL.r.sm, border: 'none', cursor: 'pointer',
            background: on ? LL.surface3 : 'transparent', color: on ? LL.fg : LL.fgMute,
            fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, whiteSpace: 'nowrap',
            boxShadow: on ? LL.shadow.sm : 'none',
            transition: `background 180ms ${LL.ease}, color 180ms ${LL.ease}`,
          }}>{text}</button>
        );
      })}
    </div>
  );
}

function IconBtn({ on, label: aria, onClick, children, tone }) {
  return (
    <button onClick={onClick} aria-label={aria} aria-pressed={on == null ? undefined : !!on} title={aria} style={{
      width: 36, height: 36, borderRadius: LL.r.sm, flexShrink: 0, cursor: 'pointer', padding: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `1px solid ${on ? 'transparent' : LL.line}`,
      background: on ? LL.accentSoft : 'transparent',
      color: on ? LL.accentText : tone === 'danger' ? LL.danger : LL.fgMute,
      transition: `background 200ms ${LL.ease}, color 200ms ${LL.ease}`,
    }}>{children}</button>
  );
}

function Row({ title, children, right }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <div style={label}>{title}</div>
        {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
      </div>
      {children}
    </div>
  );
}

function RecoveryGrid({ status }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px 8px' }}>
      {GEN_MUSCLES.map(m => {
        const s = status[m];
        const pct = s ? s.recoveryPct : 100;
        const c = pct >= 90 ? LL.accent : pct >= 60 ? `color-mix(in oklab, ${LL.accent} 55%, ${LL.warn})` : LL.warn;
        return (
          <div key={m} title={`${m}: ${pct}% recovered`} style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
              <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, color: LL.fgMute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m === 'Hamstrings' ? 'Hams' : m === 'Shoulders' ? 'Delts' : m}</span>
            </div>
            <div style={{ height: 4, borderRadius: LL.r.xs, background: LL.surface3, marginTop: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: c, borderRadius: LL.r.xs, transition: `width 260ms ${LL.ease}` }} />
            </div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: pct < 60 ? LL.warn : LL.fgDim, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{pct}%</div>
          </div>
        );
      })}
    </div>
  );
}

// ——— The sheet ——————————————————————————————————————————————————————————————

export function GenerateWorkoutSheet({ open, onClose }) {
  return (
    <LLSheet open={open} onClose={onClose} title="Build a workout" subtitle="Fits your recovery and time" maxHeight="94%">
      {open && <GeneratorBody onClose={onClose} />}
    </LLSheet>
  );
}

function GeneratorBody({ onClose }) {
  const { state, actions, setRoute, toast } = useApp();
  const units = state.prefs.units;
  const ul = unitLabel(units);
  const [opts, setOpts] = useGeneratorOpts();
  const profile = state.profile || {};
  const preset = equipmentPresetOf(profile);
  const equipment = resolveEquipment(profile);
  const [locked, setLocked] = React.useState([]);     // ids kept across regenerations
  const [swapIn, setSwapIn] = React.useState([]);     // ids chosen from "swap"
  const [excluded, setExcluded] = React.useState([]); // ids removed for today
  const [seed, setSeed] = React.useState(0);
  const [swapFor, setSwapFor] = React.useState(null); // exerciseId with the swap panel open
  const [equipOpen, setEquipOpen] = React.useState(false);
  const [showRecovery, setShowRecovery] = React.useState(false);

  const live = state.sessions.find(s => s.id === state.activeSessionId && !s.completedAt) || null;
  const injuries = injuryTags(profile.injuries);

  const plan = React.useMemo(() => generateWorkout(state, {
    ...opts, include: [...locked, ...swapIn.filter(id => !locked.includes(id))], locked, exclude: excluded, seed,
  }), [state.sessions, state.exercises, state.profile, state.prefs.units, state.prefs.plates, state.prefs.barWeight, opts.minutes, opts.goal, opts.focus, opts.muscles.join(), locked.join(), swapIn.join(), excluded.join(), seed]); // eslint-disable-line react-hooks/exhaustive-deps

  const alts = React.useMemo(() => {
    if (!swapFor) return [];
    try { return alternativesFor(state, plan, swapFor, { goal: opts.goal, exclude: excluded, limit: 4 }); } catch { return []; }
  }, [swapFor, plan]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (patch) => {
    if (patch.focus && patch.focus !== opts.focus) { setSwapIn([]); setExcluded([]); setSwapFor(null); }
    setOpts(patch);
  };
  const toggleLock = (id) => setLocked(l => (l.includes(id) ? l.filter(x => x !== id) : [...l, id]));
  const swapTo = (fromId, toId) => {
    setExcluded(x => [...new Set([...x, fromId])]);
    setSwapIn(x => [...x.filter(y => y !== fromId), toId]);
    setLocked(l => l.filter(x => x !== fromId));
    setSwapFor(null);
  };
  const removeToday = (id) => {
    setExcluded(x => [...new Set([...x, id])]);
    setSwapIn(x => x.filter(y => y !== id));
    setLocked(l => l.filter(x => x !== id));
    setSwapFor(null);
  };
  const avoidForever = (it) => {
    actions.setProfile({ avoidExercises: [...new Set([...(profile.avoidExercises || []), it.exerciseId])] });
    removeToday(it.exerciseId);
    toast(`${it.name} won't be suggested again`, 'default', { action: { label: 'Undo', run: () => actions.setProfile({ avoidExercises: (profile.avoidExercises || []).filter(x => x !== it.exerciseId) }) } });
  };
  const setPreset = (id) => {
    const p = EQUIPMENT_PRESETS.find(x => x.id === id);
    if (p.tags) actions.setProfile({ equipmentPreset: id, equipment: p.tags });
    else actions.setProfile({ equipmentPreset: 'custom', equipment });
  };
  const toggleTag = (t) => {
    const next = equipment.includes(t) ? equipment.filter(x => x !== t) : [...equipment, t];
    actions.setProfile({ equipmentPreset: 'custom', equipment: next });
  };

  const start = () => {
    if (!plan.items.length) return;
    try { unlockAudioContext(); } catch {}
    if (live) { toast('Finish your current workout first'); return; }
    const { slots, exercises } = planToSlots(plan, () => slotId());
    const have = new Set(state.exercises.map(e => e.id));
    const add = exercises.filter(e => !have.has(e.id));
    if (add.length) actions.ensureExercises(add);
    const id = actions.startFreestyle(plan.name);
    if (!id) { toast('Finish your current workout first'); return; }
    actions.updateSessionSlots(id, () => slots);
    actions.updateSession(id, { generated: { minutes: plan.minutes, goal: plan.goal, focus: plan.focus, estMinutes: plan.estMinutes } });
    onClose();
    setRoute('active-workout', { freestyle: plan.name });
  };

  const presetLabel = EQUIPMENT_PRESETS.find(x => x.id === preset)?.label || 'Custom';
  const equipSummary = preset === 'custom'
    ? (equipment.length ? EQUIPMENT_TAGS.filter(t => equipment.includes(t.id)).map(t => t.label).join(', ') : 'No equipment')
    : EQUIPMENT_PRESETS.find(x => x.id === preset)?.sub;
  const anim = reduced() ? undefined : `llgIn 240ms ${LL.ease} both`;

  return (
    <div>
      <style>{`@keyframes llgIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
        .llg-scroll::-webkit-scrollbar{display:none}`}</style>

      {/* ——— Inputs ——— */}
      <Row title="Time">
        <SSSegmented full value={opts.minutes} onChange={v => change({ minutes: v })} options={TIME_OPTIONS.map(m => [m, `${m} min`])} />
      </Row>
      <Row title="Goal">
        <SSSegmented full value={opts.goal} onChange={v => change({ goal: v })} options={GOAL_OPTIONS.map(g => [g.id, g.label])} />
      </Row>
      <Row title="Focus">
        <div className="llg-scroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '0 -18px', padding: '0 18px 2px', scrollbarWidth: 'none' }}>
          {FOCUS_OPTIONS.map(f => <Chip key={f.id} on={opts.focus === f.id} onClick={() => change({ focus: f.id })}>{f.label}</Chip>)}
        </div>
        {opts.focus === 'muscles' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {GEN_MUSCLES.map(m => {
              const on = opts.muscles.includes(m);
              return <Chip key={m} on={on} onClick={() => change({ muscles: on ? opts.muscles.filter(x => x !== m) : [...opts.muscles, m] })}>{m}</Chip>;
            })}
          </div>
        )}
      </Row>

      {/* Equipment (saved to the training profile) */}
      <div style={{ marginTop: 14, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: LL.surface2, overflow: 'hidden' }}>
        <button onClick={() => setEquipOpen(o => !o)} aria-expanded={equipOpen} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ width: 32, height: 32, borderRadius: LL.r.sm, background: LL.surface3, color: LL.fgMute, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><LLIcon.dumbbell style={{ width: 18, height: 18 }} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{presetLabel}</div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {equipSummary}{injuries.length ? ` · sparing ${injuries.map(t => INJURY_LABEL[t]).join(', ')}` : ''}
            </div>
          </span>
          <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.accentText }}>{equipOpen ? 'Done' : 'Change'}</span>
        </button>
        {equipOpen && (
          <div style={{ padding: '2px 12px 12px', borderTop: `1px solid ${LL.line}`, animation: anim }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
              {EQUIPMENT_PRESETS.map(p => {
                const on = preset === p.id;
                return (
                  <button key={p.id} onClick={() => setPreset(p.id)} aria-pressed={on} style={{
                    padding: '10px 11px', borderRadius: LL.r.sm, textAlign: 'left', cursor: 'pointer',
                    border: `1.5px solid ${on ? LL.accent : LL.line}`, background: on ? `color-mix(in oklab, ${LL.accent} 10%, ${LL.surface})` : LL.surface,
                  }}>
                    <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.fg }}>{p.label}</div>
                    <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: LL.fgMute, marginTop: 2, lineHeight: 1.35 }}>{p.sub}</div>
                  </button>
                );
              })}
            </div>
            {preset === 'custom' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                {EQUIPMENT_TAGS.map(t => <Chip key={t.id} on={equipment.includes(t.id)} onClick={() => toggleTag(t.id)}>{t.label}</Chip>)}
              </div>
            )}
            {(profile.avoidExercises || []).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ ...label, marginBottom: 6 }}>Never suggested</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {profile.avoidExercises.map(id => {
                    const ex = state.exercises.find(e => e.id === id);
                    return (
                      <button key={id} onClick={() => actions.setProfile({ avoidExercises: profile.avoidExercises.filter(x => x !== id) })} aria-label={`Allow ${ex?.name || id} again`} style={{
                        height: 30, padding: '0 6px 0 11px', borderRadius: LL.r.pill, border: `1px solid ${LL.line}`, background: LL.surface, color: LL.fgMute,
                        fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                      }}>{ex?.name || 'Exercise'}<LLIcon.x style={{ width: 14, height: 14 }} /></button>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim, marginTop: 10, lineHeight: 1.4 }}>
              Saved to your training profile. Injuries are set in Settings › Training profile.
            </div>
          </div>
        )}
      </div>

      {/* ——— Plan ——— */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, margin: '22px 0 10px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={kicker}>Today's plan</div>
          <div style={{ fontFamily: LL.display, fontSize: LL.fs.h2, fontWeight: 800, color: LL.fg, letterSpacing: -0.5, lineHeight: 1.05, marginTop: 4 }}>{plan.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
            <ClockIcon style={{ color: LL.accentText }} />
            <span><b style={{ color: LL.fg, fontWeight: 700 }}>~{plan.estMinutes} min</b> · {plural(plan.items.length, 'exercise')} · {plural(plan.totalSets, 'set')}</span>
          </div>
        </div>
        <button onClick={() => { setSeed(s => s + 1); setSwapFor(null); }} disabled={!plan.items.length} aria-label="Shuffle unlocked exercises" style={{
          height: 38, padding: '0 12px', borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg,
          fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', flexShrink: 0,
        }}><ShuffleIcon /> Shuffle</button>
      </div>

      {/* Recovery + notes */}
      <div style={{ borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: LL.surface2, padding: '10px 12px' }}>
        <button onClick={() => setShowRecovery(v => !v)} aria-expanded={showRecovery} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg, flex: 1 }}>
            {plan.readiness ? `Readiness ${plan.readiness.score} · ${plan.readiness.label}` : 'Recovery by muscle'}
          </span>
          <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute }}>{showRecovery ? 'Hide' : 'Recovery'}</span>
          <LLIcon.chevDown style={{ width: 16, height: 16, color: LL.fgFaint, transform: showRecovery ? 'rotate(180deg)' : 'none', transition: `transform 200ms ${LL.ease}` }} />
        </button>
        {showRecovery && <div style={{ marginTop: 12, animation: anim }}><RecoveryGrid status={plan.status} /></div>}
        {plan.notes.map((n, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginTop: 8, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, lineHeight: 1.45 }}>
            <span style={{ width: 5, height: 5, borderRadius: LL.r.xs, background: LL.warn, marginTop: 6, flexShrink: 0 }} />{n}
          </div>
        ))}
      </div>

      {plan.items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '28px 12px 8px' }}>
          <div style={{ width: 48, height: 48, borderRadius: LL.r.md, background: LL.surface2, color: LL.fgMute, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><LLIcon.dumbbell /></div>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg, marginTop: 10 }}>Nothing fits these choices</div>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 4, lineHeight: 1.45 }}>
            {opts.focus === 'muscles' && !opts.muscles.length ? 'Pick at least one muscle above.' : 'Try another focus, or tell the builder about more equipment.'}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {plan.items.map((it, i) => (
            <PlanItem key={it.exerciseId} it={it} i={i} units={units} ul={ul} anim={anim}
              locked={locked.includes(it.exerciseId)} onLock={() => toggleLock(it.exerciseId)}
              swapOpen={swapFor === it.exerciseId} onSwap={() => setSwapFor(s => (s === it.exerciseId ? null : it.exerciseId))}
              alts={swapFor === it.exerciseId ? alts : []}
              onPick={(toId) => swapTo(it.exerciseId, toId)}
              onRemove={() => removeToday(it.exerciseId)}
              onAvoid={() => avoidForever(it)}
              canRemove={plan.items.length > 1} />
          ))}
        </div>
      )}

      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim, lineHeight: 1.45, margin: '14px 2px 0' }}>
        Time includes warm-up ramps, rest and setup. Loads come from your recent estimated 1RMs; lifts you haven't logged start with finding a weight.
      </div>

      {/* Sticky start */}
      <div style={{ position: 'sticky', bottom: -20, margin: '14px -18px -20px', padding: '14px 18px calc(16px + env(safe-area-inset-bottom, 0px))',
        background: `linear-gradient(180deg, color-mix(in oklab, ${LL.surface} 0%, transparent) 0%, ${LL.surface} 26%)` }}>
        {live ? (
          <button onClick={() => { onClose(); setRoute('active-workout', { dayId: live.dayId || undefined }); }} style={startBtn(false)}>
            <LLIcon.play style={{ width: 18, height: 18 }} /> Resume {live.name || 'workout'} first
          </button>
        ) : (
          <button onClick={start} disabled={!plan.items.length} style={startBtn(!plan.items.length)}>
            <LLIcon.play style={{ width: 18, height: 18 }} /> Start workout{plan.items.length ? ` · ${plan.estMinutes} min` : ''}
          </button>
        )}
      </div>
    </div>
  );
}

function startBtn(disabled) {
  return {
    width: '100%', height: 54, borderRadius: LL.r.md, border: 'none', cursor: disabled ? 'default' : 'pointer',
    background: disabled ? LL.surface3 : LL.accent, color: disabled ? LL.fgDim : LL.accentInk,
    fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    boxShadow: disabled ? 'none' : LL.shadow.md,
  };
}

function rxMain(it, units, ul) {
  const reps = it.timed ? `${it.repMin}–${it.repMax} s` : it.repMin === it.repMax ? `${it.repMin}` : `${it.repMin}–${it.repMax}`;
  const load = it.load > 0 ? `${it.bodyweight ? '+' : ''}${fmtWeight(it.load, units)} ${ul}` : it.bodyweight && !it.timed ? 'bodyweight' : null;
  return { sets: `${it.sets} × ${reps}`, load };
}
function rxMeta(it) {
  return [`RPE ${it.rpe}`, `${fmtRest(it.rest)} rest`, it.warmups > 0 ? `+${plural(it.warmups, 'warm-up')}` : null].filter(Boolean);
}

function PlanItem({ it, i, units, ul, anim, locked, onLock, swapOpen, onSwap, alts, onPick, onRemove, onAvoid, canRemove }) {
  return (
    <div style={{
      borderRadius: LL.r.md, background: LL.surface, overflow: 'hidden',
      border: `1px solid ${locked ? `color-mix(in oklab, ${LL.accent} 45%, ${LL.line})` : LL.line}`,
      animation: anim, animationDelay: anim ? `${Math.min(i, 8) * 30}ms` : undefined,
    }}>
      <div style={{ display: 'flex', gap: 12, padding: '12px 10px 12px 12px' }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <MuscleThumb primary={it.primary} secondary={it.secondary} size={44} />
          <span style={{ position: 'absolute', left: -5, top: -5, minWidth: 20, height: 20, padding: '0 5px', boxSizing: 'border-box', borderRadius: LL.r.pill,
            background: locked ? LL.accent : LL.surface3, color: locked ? LL.accentInk : LL.fgMute, border: `2px solid ${LL.surface}`,
            fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg, lineHeight: 1.25 }}>{it.name}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 8, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg, letterSpacing: 0.1 }}>{rxMain(it, units, ul).sets}</span>
            {rxMain(it, units, ul).load && <span style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.accentText }}>{rxMain(it, units, ul).load}</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 4, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
            {rxMeta(it).map((t, k, a) => <span key={k} style={{ whiteSpace: 'nowrap' }}>{t}{k < a.length - 1 ? ' ·' : ''}</span>)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <IconBtn on={locked} label={locked ? `Unlock ${it.name}` : `Lock ${it.name}`} onClick={onLock}><LLIcon.lock style={{ width: 17, height: 17 }} /></IconBtn>
          <IconBtn on={swapOpen} label={`Swap ${it.name}`} onClick={onSwap}><LLIcon.swap style={{ width: 17, height: 17 }} /></IconBtn>
        </div>
      </div>
      <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', gap: 8, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, lineHeight: 1.45 }}>
          <LLIcon.target style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2, color: LL.accentText }} />
          <span>{it.why}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, fontFamily: LL.ui, fontSize: LL.fs.sm, lineHeight: 1.45, color: it.firstTime && !it.bodyweight ? LL.prText : LL.fgMute }}>
          <LLIcon.dumbbell style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2, color: it.firstTime && !it.bodyweight ? LL.prText : LL.fgDim }} />
          <span>{it.loadWhy}</span>
        </div>
      </div>
      {swapOpen && (
        <div style={{ borderTop: `1px solid ${LL.line}`, background: LL.surface2, padding: '10px 12px 12px', animation: anim }}>
          <div style={{ ...kicker, marginBottom: 8 }}>Swap for</div>
          {alts.length === 0 && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute }}>No other {it.muscle.toLowerCase()} exercise fits your equipment.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {alts.map(a => (
              <button key={a.exerciseId} onClick={() => onPick(a.exerciseId)} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: LL.r.sm, cursor: 'pointer', textAlign: 'left',
                border: `1px solid ${LL.line}`, background: LL.surface,
              }}>
                <MuscleThumb primary={a.primary} secondary={a.secondary} size={34} radius={8} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg }}>{a.name}</div>
                  <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: a.firstTime && !a.bodyweight ? LL.prText : LL.fgMute, marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>
                    {a.load > 0 ? `${a.bodyweight ? '+' : ''}${fmtWeight(a.load, units)} ${ul} × ${a.reps}` : a.firstTime && !a.bodyweight ? 'First time — find a weight' : a.bodyweight ? 'Bodyweight' : ''}
                    {a.history ? ` · done ${a.history.exposures}×` : ''}
                  </div>
                </span>
                <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, color: LL.accentText }}>Use</span>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {canRemove && <button onClick={onRemove} style={ghostSmall}>Remove for today</button>}
            <button onClick={onAvoid} style={{ ...ghostSmall, color: LL.danger }}>Never suggest</button>
          </div>
        </div>
      )}
    </div>
  );
}

const ghostSmall = {
  flex: 1, height: 38, borderRadius: LL.r.sm, border: `1px solid ${LL.line2}`, background: 'transparent', color: LL.fg,
  fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer',
};
